import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../../contexts/AuthContext';
import { useLanguage } from '../../contexts/LanguageContext';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Card } from '../ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import { Label } from '../ui/label';
import { toast } from 'sonner';
import { ArrowLeft, Plus, X } from 'lucide-react';
import { canWrite } from '../../utils/permissions';
import { formatDate } from '../../utils/dateFormatter';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const SupplierDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { getAuthHeader, user } = useAuth();
  const { t, language } = useLanguage();
  const [supplier, setSupplier] = useState(null);
  const [payments, setPayments] = useState([]);
  const [etaInvoices, setEtaInvoices] = useState([]);
  const [isPaymentOpen, setIsPaymentOpen] = useState(false);
  const [isPaymentSubmitting, setIsPaymentSubmitting] = useState(false);
  const [yearFilter, setYearFilter] = useState('all');
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const [paymentData, setPaymentData] = useState({
    amount: 0,
    date: new Date().toISOString().split('T')[0],
    notes: '',
    payment_method: 'cash'
  });

  const fetchSupplierDetails = async () => {
    try {
      const response = await axios.get(`${API}/suppliers/by-id/${id}`, { headers: getAuthHeader() });
      setSupplier(response.data.supplier);
      setPayments(response.data.payments);
      if (response.data.supplier.registration_number) {
        const etaRes = await axios.get(`${API}/eta/received?issuer_id=${encodeURIComponent(response.data.supplier.registration_number)}`, { headers: getAuthHeader() });
        setEtaInvoices(etaRes.data || []);
      }
    } catch (error) {
      toast.error('Failed to fetch supplier details');
      navigate('/finance');
    }
  };

  useEffect(() => {
    fetchSupplierDetails();
  }, [id]);

  const handleAddPayment = async (e) => {
    e.preventDefault();
    if (isPaymentSubmitting) return;
    setIsPaymentSubmitting(true);
    try {
      await axios.post(`${API}/payments`, {
        entity_id: supplier.name,
        entity_type: 'supplier',
        ...paymentData
      }, { headers: getAuthHeader() });
      toast.success('Payment recorded');
      setIsPaymentOpen(false);
      fetchSupplierDetails();
      setPaymentData({ amount: 0, date: new Date().toISOString().split('T')[0], notes: '', payment_method: 'cash' });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to record payment');
    } finally {
      setIsPaymentSubmitting(false);
    }
  };

  const handleDeletePayment = async (paymentId) => {
    if (!window.confirm('Delete this payment?')) return;
    try {
      await axios.delete(`${API}/payments/delete/${paymentId}`, { headers: getAuthHeader() });
      toast.success('Payment deleted');
      fetchSupplierDetails();
    } catch {
      toast.error('Failed to delete payment');
    }
  };

  if (!supplier) return <div className="p-8">Loading...</div>;

  const availableYears = [...new Set(payments.map(p => (p.date || '').slice(0, 4)).filter(Boolean))].sort().reverse();

  const filteredPayments = payments.filter(p =>
    yearFilter === 'all' || (p.date || '').slice(0, 4) === yearFilter
  );

  const totalInvoiced = etaInvoices.reduce((sum, inv) => sum + (inv.total || 0), 0);
  const totalPayments = payments.reduce((sum, p) => sum + p.amount, 0);
  const balanceOwed = totalInvoiced - totalPayments;

  return (
    <div className="p-8 space-y-6">
      <div className="flex items-center gap-4">
        <Button variant="ghost" onClick={() => navigate('/finance')} data-testid="back-to-finance-btn">
          <ArrowLeft className="w-4 h-4" />
        </Button>
        <h1 className="text-4xl font-bold tracking-tight" lang={language === 'ar' ? 'ar' : 'en'} data-testid="supplier-detail-title">
          {supplier.name}
        </h1>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card className="p-6 border border-border">
          <h3 className="text-lg font-semibold mb-4" lang={language === 'ar' ? 'ar' : 'en'}>Contact Information</h3>
          <div className="space-y-2 text-sm">
            {supplier.registration_number && <div><span className="text-muted-foreground">Reg. No:</span><p className="font-medium">{supplier.registration_number}</p></div>}
            <div><span className="text-muted-foreground">{t('email')}:</span><p className="font-medium">{supplier.email || 'N/A'}</p></div>
            <div><span className="text-muted-foreground">Phone:</span><p className="font-medium">{supplier.phone || 'N/A'}</p></div>
            <div><span className="text-muted-foreground">Address:</span><p className="font-medium">{supplier.address || 'N/A'}</p></div>
          </div>
        </Card>

        <Card className="p-6 border border-border">
          <h3 className="text-lg font-semibold mb-4">Financial Summary</h3>
          <div className="space-y-2 text-sm">
            <div className="flex justify-between">
              <span className="text-muted-foreground">Total Invoiced:</span>
              <span className="font-mono-numeric font-semibold text-destructive">{totalInvoiced.toFixed(2)} {t('currency')}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-muted-foreground">Total Paid:</span>
              <span className="font-mono-numeric font-semibold text-success">{totalPayments.toFixed(2)} {t('currency')}</span>
            </div>
            <div className="flex justify-between border-t border-border pt-2 mt-2">
              <span className="font-semibold">Balance Owed:</span>
              <span className={`font-mono-numeric font-bold ${balanceOwed > 0 ? 'text-warning' : 'text-success'}`}>{balanceOwed.toFixed(2)} {t('currency')}</span>
            </div>
          </div>
        </Card>
      </div>

      <Tabs defaultValue="invoices" className="w-full">
        <TabsList className="mb-4">
          <TabsTrigger value="invoices">Invoices</TabsTrigger>
          <TabsTrigger value="payments">{t('payments')}</TabsTrigger>
        </TabsList>

        <TabsContent value="invoices">
          <Card className="p-6 border border-border">
            <h2 className="text-2xl font-semibold mb-6">Invoices</h2>
            <div className="overflow-x-auto">
              <table className="w-full text-sm border-collapse">
                <thead className="bg-muted/50">
                  <tr>
                    <th className="border border-border p-3 text-start font-semibold">Date Issued</th>
                    <th className="border border-border p-3 text-start font-semibold">Date Received</th>
                    <th className="border border-border p-3 text-start font-semibold">Invoice #</th>
                    <th className="border border-border p-3 text-start font-semibold">Type</th>
                    <th className="border border-border p-3 text-end font-semibold">{t('total')}</th>
                    <th className="border border-border p-3 text-center font-semibold">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {etaInvoices.map((inv, idx) => (
                    <tr key={idx} className="hover:bg-muted/30 cursor-pointer" onClick={() => setSelectedInvoice(inv)}>
                      <td className="border border-border p-3">{formatDate(inv.date_issued) || '—'}</td>
                      <td className="border border-border p-3">{formatDate(inv.date_received) || '—'}</td>
                      <td className="border border-border p-3 font-mono text-xs">{inv.internal_id || '—'}</td>
                      <td className="border border-border p-3 text-xs">{inv.type_name || '—'}</td>
                      <td className="border border-border p-3 text-end font-mono-numeric font-semibold">{inv.total != null ? inv.total.toFixed(2) : '—'} {t('currency')}</td>
                      <td className="border border-border p-3 text-center">
                        <span className={`text-xs px-2 py-1 rounded-full font-medium ${inv.status === 'Valid' ? 'bg-green-100 text-green-800' : inv.status === 'Invalid' ? 'bg-red-100 text-red-800' : 'bg-gray-100 text-gray-600'}`}>
                          {inv.status || '—'}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {etaInvoices.length === 0 && <div className="text-center py-8 text-muted-foreground">No invoices found — upload ETA JSON files from Finance → ETA Received</div>}
            </div>
          </Card>
        </TabsContent>

        <TabsContent value="payments">
          <Card className="p-6 border border-border">
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-2xl font-semibold">{t('payments')}</h2>
              {canWrite(user?.role) && <Dialog open={isPaymentOpen} onOpenChange={setIsPaymentOpen}>
                <DialogTrigger asChild>
                  <Button className="gap-2 bg-accent hover:bg-accent/90" data-testid="add-payment-btn">
                    <Plus className="w-4 h-4" />
                    Record Payment
                  </Button>
                </DialogTrigger>
                <DialogContent>
                  <DialogHeader>
                    <DialogTitle>Record Payment</DialogTitle>
                  </DialogHeader>
                  <form onSubmit={handleAddPayment} className="space-y-4">
                    <div>
                      <Label>Amount ({t('currency')})</Label>
                      <Input type="number" step="0.01" value={paymentData.amount} onChange={(e) => setPaymentData({ ...paymentData, amount: parseFloat(e.target.value) })} required data-testid="payment-amount-input" />
                    </div>
                    <div>
                      <Label>{t('date')}</Label>
                      <Input type="date" value={paymentData.date} onChange={(e) => setPaymentData({ ...paymentData, date: e.target.value })} required data-testid="payment-date-input" />
                    </div>
                    <div>
                      <Label>Payment Method</Label>
                      <div className="flex gap-2 mt-1">
                        <button type="button" onClick={() => setPaymentData({ ...paymentData, payment_method: 'cash' })}
                          className={`flex-1 py-2 rounded-md border text-sm font-medium transition-colors ${paymentData.payment_method === 'cash' ? 'bg-accent text-accent-foreground border-accent' : 'border-input bg-background'}`}>
                          Cash
                        </button>
                        <button type="button" onClick={() => setPaymentData({ ...paymentData, payment_method: 'bank' })}
                          className={`flex-1 py-2 rounded-md border text-sm font-medium transition-colors ${paymentData.payment_method === 'bank' ? 'bg-accent text-accent-foreground border-accent' : 'border-input bg-background'}`}>
                          Bank Transfer
                        </button>
                      </div>
                    </div>
                    <div>
                      <Label>{t('notes')}</Label>
                      <Input value={paymentData.notes} onChange={(e) => setPaymentData({ ...paymentData, notes: e.target.value })} data-testid="payment-notes-input" />
                    </div>
                    <Button type="submit" className="w-full bg-accent hover:bg-accent/90" data-testid="payment-submit-btn" disabled={isPaymentSubmitting}>{t('save')}</Button>
                  </form>
                </DialogContent>
              </Dialog>}
            </div>
            <div className="flex justify-end mb-3">
              <select
                value={yearFilter}
                onChange={e => setYearFilter(e.target.value)}
                className="h-9 rounded-md border border-input bg-background px-3 text-sm"
              >
                <option value="all">All Years</option>
                {availableYears.map(y => <option key={y} value={y}>{y}</option>)}
              </select>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm border-collapse">
                <thead className="bg-muted/50">
                  <tr>
                    <th className="border border-border p-3 text-start font-semibold">{t('date')}</th>
                    <th className="border border-border p-3 text-end font-semibold">Amount</th>
                    <th className="border border-border p-3 text-start font-semibold">Method</th>
                    <th className="border border-border p-3 text-start font-semibold">{t('notes')}</th>
                    {user?.role === 'admin' && <th className="border border-border p-3" />}
                  </tr>
                </thead>
                <tbody>
                  {filteredPayments.map((payment, idx) => (
                    <tr key={idx} className="hover:bg-muted/30" data-testid={`payment-row-${idx}`}>
                      <td className="border border-border p-3">{formatDate(payment.date)}</td>
                      <td className="border border-border p-3 text-end font-mono-numeric text-destructive">{payment.amount.toFixed(2)} {t('currency')}</td>
                      <td className="border border-border p-3 capitalize">{payment.payment_method || 'cash'}</td>
                      <td className="border border-border p-3">{payment.notes || '-'}</td>
                      {user?.role === 'admin' && (
                        <td className="border border-border p-3 text-center">
                          <button onClick={() => handleDeletePayment(payment.id)} className="text-destructive hover:text-destructive/70">
                            <X className="w-4 h-4" />
                          </button>
                        </td>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
              {filteredPayments.length === 0 && <div className="text-center py-8 text-muted-foreground">No payments recorded yet</div>}
            </div>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Invoice Detail Modal */}
      {selectedInvoice && (() => {
        let doc = null;
        try {
          const raw = selectedInvoice.raw_json;
          doc = typeof raw?.document === 'string' ? JSON.parse(raw.document) : raw?.document;
        } catch {}
        const lines = doc?.invoiceLines || [];
        const taxTotals = doc?.taxTotals || [];
        const totalSales = doc?.totalSalesAmount ?? selectedInvoice.net_amount;
        const totalDiscount = doc?.totalDiscountAmount ?? 0;
        const totalAmount = doc?.totalAmount ?? selectedInvoice.total;
        return (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="bg-background border border-border rounded-lg w-full max-w-3xl max-h-[90vh] overflow-y-auto p-6 space-y-4">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="text-lg font-semibold">Invoice {selectedInvoice.internal_id}</h3>
                  <p className="text-xs text-muted-foreground">{selectedInvoice.eta_uuid}</p>
                </div>
                <button onClick={() => setSelectedInvoice(null)} className="text-muted-foreground hover:text-foreground"><X className="w-5 h-5" /></button>
              </div>

              <div className="grid grid-cols-2 gap-4 text-sm">
                <div><span className="text-muted-foreground">Date Issued:</span> <span>{formatDate(selectedInvoice.date_issued) || '—'}</span></div>
                <div><span className="text-muted-foreground">Date Received:</span> <span>{formatDate(selectedInvoice.date_received) || '—'}</span></div>
                <div><span className="text-muted-foreground">Type:</span> <span>{selectedInvoice.type_name}</span></div>
                <div><span className="text-muted-foreground">Status:</span> <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${selectedInvoice.status === 'Valid' ? 'bg-green-100 text-green-800' : selectedInvoice.status === 'Invalid' ? 'bg-red-100 text-red-800' : 'bg-gray-100 text-gray-600'}`}>{selectedInvoice.status}</span></div>
              </div>

              {lines.length > 0 && (
                <div className="overflow-x-auto">
                  <table className="w-full text-xs border-collapse">
                    <thead className="bg-muted/50">
                      <tr>
                        <th className="border border-border p-2 text-start">Description</th>
                        <th className="border border-border p-2 text-end">Qty</th>
                        <th className="border border-border p-2 text-end">Unit Price</th>
                        <th className="border border-border p-2 text-end">Discount</th>
                        <th className="border border-border p-2 text-end">VAT</th>
                        <th className="border border-border p-2 text-end">Total</th>
                      </tr>
                    </thead>
                    <tbody>
                      {lines.map((line, i) => (
                        <tr key={i} className="hover:bg-muted/30">
                          <td className="border border-border p-2">{line.description}</td>
                          <td className="border border-border p-2 text-end">{line.quantity} {line.unitType}</td>
                          <td className="border border-border p-2 text-end font-mono-numeric">{line.unitValue?.amountEGP?.toFixed(2) ?? '—'}</td>
                          <td className="border border-border p-2 text-end font-mono-numeric">{line.discount?.amount > 0 ? line.discount.amount.toFixed(2) : '—'}</td>
                          <td className="border border-border p-2 text-end font-mono-numeric">{line.taxableItems?.[0]?.amount?.toFixed(2) ?? '—'} ({line.taxableItems?.[0]?.rate ?? 0}%)</td>
                          <td className="border border-border p-2 text-end font-mono-numeric font-semibold">{line.total?.toFixed(2) ?? '—'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

              <div className="bg-muted/30 p-3 rounded-md text-sm space-y-1">
                <div className="flex justify-between"><span className="text-muted-foreground">Subtotal:</span><span className="font-mono-numeric">{totalSales?.toFixed(2)} {t('currency')}</span></div>
                {totalDiscount > 0 && <div className="flex justify-between"><span className="text-muted-foreground">Discount:</span><span className="font-mono-numeric">-{totalDiscount.toFixed(2)} {t('currency')}</span></div>}
                {taxTotals.map((tax, i) => (
                  <div key={i} className="flex justify-between"><span className="text-muted-foreground">VAT ({tax.taxType}):</span><span className="font-mono-numeric">{tax.amount?.toFixed(2)} {t('currency')}</span></div>
                ))}
                <div className="flex justify-between font-semibold border-t border-border pt-1"><span>Total:</span><span className="font-mono-numeric">{totalAmount?.toFixed(2)} {t('currency')}</span></div>
              </div>
            </div>
          </div>
        );
      })()}
    </div>
  );
};
